import { CloseOutlined, SearchOutlined, UserOutlined } from "@ant-design/icons";
import { Input } from "antd";
import { useEffect, useMemo, useRef, useState } from "react";
import { useAppDispatch, useAppSelector, useDarkMode, useThemeColors } from "redux/hooks";
import { setAdminViewModalOpen, setAdminViewUserID } from "redux/slices/user";

// Modal-style picker for admin "view as" mode. Lists every user in
// state.user.users (excluding the admin themselves), filtered by a search box.
// Selecting a user dispatches setAdminViewUserID + closes; a "Leave admin
// view" row appears when one is currently active.
export function AdminViewModal() {
    const dispatch = useAppDispatch();
    const isOpen = useAppSelector(state => state.user.adminViewModalOpen);
    const users = useAppSelector(state => state.user.users);
    const myUserID = useAppSelector(state => state.user.userID);
    const adminViewUserID = useAppSelector(state => state.user.adminViewUserID);
    const themeColors = useThemeColors();
    const darkMode = useDarkMode();
    const cardRef = useRef<HTMLDivElement>(null);
    const [filter, setFilter] = useState("");

    useEffect(() => {
        if (!isOpen) return;
        setFilter("");
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) return;
        const handleClickOutside = (e: MouseEvent) => {
            if (cardRef.current && !cardRef.current.contains(e.target as Node)) {
                dispatch(setAdminViewModalOpen({ open: false }));
            }
        };
        const t = setTimeout(() => document.addEventListener("mousedown", handleClickOutside), 0);
        return () => {
            clearTimeout(t);
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isOpen, dispatch]);

    const filtered = useMemo(() => {
        const q = filter.trim().toLowerCase();
        return (users || [])
            .filter(u => u.userID !== myUserID)
            .filter(u => !q || u.name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q) || u.userID?.toLowerCase().includes(q))
            .sort((a, b) => (a.name || a.userID).localeCompare(b.name || b.userID));
    }, [users, myUserID, filter]);

    if (!isOpen) return null;

    const close = () => dispatch(setAdminViewModalOpen({ open: false }));
    const selectUser = (userID: string) => {
        dispatch(setAdminViewUserID({ adminViewUserID: userID }));
        close();
    };
    const leaveAdminView = () => {
        dispatch(setAdminViewUserID({ adminViewUserID: "" }));
        close();
    };

    return (
        <>
            <style>{`
                .admin-view-row:hover {
                    background-color: ${themeColors.hoverBackground} !important;
                }
            `}</style>
            <div
                onClick={close}
                style={{
                    position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: "rgba(0, 0, 0, 0.5)",
                    zIndex: 2000,
                    display: "flex", justifyContent: "center", alignItems: "center",
                }}
            >
                <div
                    ref={cardRef}
                    onClick={(e) => e.stopPropagation()}
                    style={{
                        backgroundColor: themeColors.backgroundColor,
                        borderRadius: "8px",
                        boxShadow: darkMode ? "0 4px 12px rgba(0, 0, 0, 0.5)" : "0 4px 12px rgba(0, 0, 0, 0.15)",
                        width: "520px",
                        maxWidth: "90vw",
                        maxHeight: "80vh",
                        display: "flex",
                        flexDirection: "column",
                    }}
                >
                    <div style={{
                        display: "flex", justifyContent: "space-between", alignItems: "center",
                        padding: "16px 20px",
                        borderBottom: `1px solid ${themeColors.borderColor}`,
                    }}>
                        <h2 style={{ margin: 0, fontSize: "1.4em", fontWeight: 600, color: themeColors.textColor }}>
                            Admin View
                        </h2>
                        <button
                            onClick={close}
                            title="Close"
                            style={{
                                background: "none", border: "none", cursor: "pointer", padding: 4,
                                display: "flex", alignItems: "center", justifyContent: "center",
                                color: themeColors.iconColor, fontSize: 18,
                            }}
                        >
                            <CloseOutlined />
                        </button>
                    </div>

                    {adminViewUserID && (
                        <div
                            onClick={leaveAdminView}
                            className="admin-view-row"
                            style={{
                                cursor: "pointer", padding: "12px 20px",
                                borderBottom: `1px solid ${themeColors.borderColor}`,
                                color: themeColors.primaryColor,
                                fontWeight: 600,
                            }}
                        >
                            Leave admin view
                        </div>
                    )}

                    <div style={{ padding: "12px 20px", borderBottom: `1px solid ${themeColors.borderColor}` }}>
                        <Input
                            value={filter}
                            onChange={e => setFilter(e.target.value)}
                            prefix={<SearchOutlined style={{ color: themeColors.iconColor }} />}
                            placeholder="Search users…"
                            allowClear
                        />
                    </div>

                    <div style={{ overflowY: "auto", flex: 1 }}>
                        {filtered.length === 0 && (
                            <div style={{ padding: "20px", color: themeColors.textColorSecondary, textAlign: "center" }}>
                                {users && users.length > 1 ? "No users match." : "No other users."}
                            </div>
                        )}
                        {filtered.map(u => {
                            const active = u.userID === adminViewUserID;
                            return (
                                <div
                                    key={u.userID}
                                    onClick={() => selectUser(u.userID)}
                                    className="admin-view-row"
                                    style={{
                                        cursor: "pointer",
                                        padding: "12px 20px",
                                        display: "flex", alignItems: "center", gap: 12,
                                        backgroundColor: active ? themeColors.selectedBackground : "transparent",
                                        borderLeft: active ? `3px solid ${themeColors.primaryColor}` : "3px solid transparent",
                                    }}
                                >
                                    <UserOutlined style={{ fontSize: 18, color: themeColors.iconColor }} />
                                    <div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
                                        <span style={{ color: themeColors.textColor, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                            {u.name || u.userID}
                                        </span>
                                        {u.email && (
                                            <span style={{ color: themeColors.textColorSecondary, fontSize: 12, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                {u.email}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </>
    );
}
